import { watch } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useGridStore } from '@/stores/grid';
import { useSheetsStore } from '@/stores/sheets';
import { useSheetCatalogStore } from '@/stores/sheetCatalog';
import { useDictionariesStore } from '@/stores/dictionaries';
import { useObservation } from './useObservation';
import { workbookTabKey } from '@/lib/workbookRoute';

/** После текущей вкладки последовательно готовим обе большие таблицы в памяти сессии. */
export function useTablePreload(): void {
  const route = useRoute();
  const auth = useAuthStore();
  const grid = useGridStore();
  const base = useSheetsStore('base');
  const catalog = useSheetCatalogStore();
  const dictionaries = useDictionariesStore();
  const observation = useObservation();
  watch(() => {
    if (!auth.isAuthenticated || observation.active.value) return null;
    const key = workbookTabKey(route);
    if (!key) return null;
    if (key === 'grid') {
      if (!grid.initialised || grid.loading || grid.error) return null;
    } else {
      const code = String(route.params.code);
      const current = useSheetsStore(code);
      if (!current.hasLoadedRows(code) || current.loading || current.refreshing) return null;
    }
    // Фоновые чтения не конкурируют друг с другом и с текущей таблицей.
    if (grid.loading || grid.refreshing || base.loading || base.refreshing) return null;
    if (key !== 'grid' && !grid.initialised && !grid.error) return 'grid';
    if (key !== 'sheet:base' && !base.detail && !base.error
      && catalog.sheets.some(sheet => sheet.code === 'base')) return 'base';
    return null;
  }, (target, _previous, onCleanup) => {
    if (!target) return;
    // Два кадра дают текущей таблице появиться до чтения соседней.
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => {
        if (target === 'base') {
          void import('@/views/SheetView.vue').catch(() => {});
          void base.enter('base');
        } else {
          void import('@/views/GridView.vue').catch(() => {});
          void dictionaries.loadAll().catch(() => {});
          void grid.enter();
        }
      });
    });
    onCleanup(() => { cancelAnimationFrame(first); cancelAnimationFrame(second); });
  }, { immediate: true, flush: 'post' });
}
